import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useEffect, useState } from "react";
import useApi from "../../../../core/customhook/useApi";
import { useNavigate } from "react-router";
import { all_routes } from "../../../router/all_routes";
import Loading from "../../../../core/common/custom-loading/Loading";
import dayjs from "dayjs";
import { SearchPayloadType } from "../FiltersTrackCase";
import webservice_url from "../../../../core/webservice/Webservice_url";
import "./caseAction.css";

type CheckStatus =
  | "VERIFICATION_IN_PROGRESS"
  | "STOPPED"
  | "CANCELLED"
  | "COMPLETED";
interface CaseActionItemsProps {
  caseDetail?: any;
  assignmentId?: string;
}
interface TableDataInterface {
  assignment_template_id: string;
  assignment_id?: string;
  case_id?: string;
  check_name?: string;
  key_value?: string;
  status?: string;
  start_date?: string;
}

const STATUS_CONFIG: Record<
  CheckStatus,
  {
    label: string;
    bgColor: string;
    color: string;
  }
> = {
  VERIFICATION_IN_PROGRESS: {
    label: "Verification In Progress",
    bgColor: "#0D6EFD",
    color: "#fff",
  },

  STOPPED: {
    label: "Stopped",
    bgColor: "#6C757D",
    color: "#fff",
  },

  CANCELLED: {
    label: "Cancelled",
    bgColor: "#DC3545",
    color: "#fff",
  },

  COMPLETED: {
    label: "Completed",
    bgColor: "#198754",
    color: "#fff",
  },
};

const CaseActionItems: React.FC<CaseActionItemsProps> = ({ assignmentId }) => {
  const navigate = useNavigate();
  const [activeCheck, setActiveCheck] = useState(null);

  const [checkHistory, setCheckHistory] = useState<TableDataInterface[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [total, setTotal] = useState(0);
  const [caseInfo, setCaseInfo] = useState<any>({});

  const getChecksHistoryAPI = useApi<any>(
    assignmentId
      ? `/api/v1/candidate/forms/case-check-summary/${assignmentId}`
      : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const [caseLogs, setCaseLogs] = useState<Record<string, any[]>>({});

  const getCaseLog = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (assignmentTemplateId?: string) => {
    if (!assignmentTemplateId) return;

    // Prevent duplicate API calls
    if (caseLogs[assignmentTemplateId]) return;

    try {
      const res = await getCaseLog.callApi(
        undefined,
        `/api/v1/candidate/forms/timeline/${assignmentTemplateId}`,
      );

      setCaseLogs((prev) => ({
        ...prev,
        [assignmentTemplateId]: res?.data || [],
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCheckHistory = async (
    searchPayload: Partial<SearchPayloadType> = {},
    page: number = pagination.page,
    limit: number = pagination.limit,
  ) => {
    if (!assignmentId) return;

    try {
      const res = await getChecksHistoryAPI.callApi();
      const historyList = Array.isArray(res?.data?.checks)
        ? res.data.checks
        : [];

      setCheckHistory(historyList);
      setTotal(historyList.length);
      setLastSearchPayload(searchPayload);

      setCaseInfo({
        case_id: res?.data?.case_id,
        candidate_name: res?.data?.candidate_name,
        case_receive_date: res?.data?.case_receive_date,
        sla_start_date: res?.data?.sla_start_date,
        due_date: res?.data?.due_date,
      });
    } catch (error) {
      console.error(error);
      setCheckHistory([]);
      setTotal(0);
      setCaseInfo({});
    }
  };

  useEffect(() => {
    if (assignmentId) {
      fetchCheckHistory();
    }
  }, [assignmentId]);

  const formatStatus = (status: string) => {
    if (!status) return "-";

    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const DownloadAssestedReport = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
    responseType: "blob",
  });

  const handleDownloadReport = async (assignmentTemplateId: any) => {
    if (!assignmentTemplateId) {
      console.error("Assignment Template ID is missing");
      return;
    }

    try {
      const response: any = await DownloadAssestedReport.callApi(
        undefined,
        `${webservice_url.DownloadAssestedReport}/${assignmentTemplateId}/download-pdf`,
      );

      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `_Report_${assignmentTemplateId}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("Report Download Error:", error);
    }
  };

  const CHECK_TYPE_CONFIG: Record<
    string,
    { label: string; icon: string; className: string }
  > = {
    ASSISTED: {
      label: "Assisted",
      icon: "ti ti-user-check",
      className: "cai-type-assisted",
    },
    NON_ASSISTED: {
      label: "Non-Assisted",
      icon: "ti ti-bolt",
      className: "cai-type-non-assisted",
    },
  };
  const CheckTypeBadge = ({ type }: { type?: string }) => {
    if (!type) return null;

    // normalises "non-assisted", "NOT_ASSISTED", "Non Assisted" etc.
    let key = type.toUpperCase().replace(/[\s-]/g, "_");
    if (key === "NOT_ASSISTED") key = "NON_ASSISTED";

    const config = CHECK_TYPE_CONFIG[key] || {
      label: type,
      icon: "ti ti-tag",
      className: "cai-type-default",
    };

    return (
      <span className={`cai-type-badge ${config.className}`}>
        <i className={config.icon}></i>
        {config.label}
      </span>
    );
  };
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const hasValue = (value: unknown): value is string => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return (
      text !== "" &&
      !["--", "-", "N/A", "NA", "null", "undefined"].includes(text)
    );
  };
  const handleCopy = async (text: string, id: string) => {
    if (!text) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // fallback for http / older browsers
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopiedKey(id);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error("Copy failed", error);
    }
  };
  return (
    <>
      <div className="overflow-x-auto w-100 cai-scroll">
        <div className="alert alert-warning mt-3 mb-3">
          <div className="d-flex align-items-start gap-2">
            <i className="ti ti-info-circle fs-5 mt-1"></i>

            <div>
              <strong>Note:</strong>
              <span className="ms-1">
                Insufficiency can be raised only for Assisted Checks.
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            minWidth: "800px",
          }}
        >
          {getChecksHistoryAPI.loading ? (
            <Loading />
          ) : (
            <>
              {checkHistory?.length > 0 ? (
                [...checkHistory]
                  .sort(
                    (a: any, b: any) =>
                      ["NON-ASSISTED", "ASSISTED"].indexOf(a.check_type) -
                      ["NON-ASSISTED", "ASSISTED"].indexOf(b.check_type),
                  )
                  .map((d: any) => (
                    <div key={d.assignment_template_id} className="mb-3">
                      <div className="bg-white border cai-row-card rounded-3 py-3 px-2 shadow-sm">
                        <div className="row g-0 align-items-center">
                          {/* Check Details */}
                          <div className="col-lg-2 px-3 border-end">
                            <div className="cai-col-label">Check</div>
                            <div className="fw-bold text-dark cai-check-name">
                              {d.check_name}
                            </div>
                            <CheckTypeBadge type={d.check_type} />
                            {d.subLabel && (
                              <small className="text-muted">{d.subLabel}</small>
                            )}
                          </div>

                          {/* Reference Details */}
                          {/* <div className="col-lg-3  px-3 border-end">
                          <div className="cai-col-label">Reference</div>
                          <div className="fw-semibold cai-key-value">
                            {d.key_value}
                          </div>

                          {d.detailsExtra && (
                            <small className="text-muted d-block">
                              {d.detailsExtra}
                            </small>
                          )}
                        </div> */}
                          <div className="col-lg-3 px-3 border-end">
                            <div className="cai-col-label">Reference</div>

                            {hasValue(d.key_value) ? (
                              <div className="d-flex align-items-center gap-2">
                                <div className="fw-semibold cai-key-value text-break">
                                  {d.key_value}
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-sm p-0 border-0 bg-transparent"
                                  title={
                                    copiedKey === d.key_value
                                      ? "Copied!"
                                      : "Copy"
                                  }
                                  onClick={() =>
                                    handleCopy(
                                      String(d.key_value).trim(),
                                      d.key_value,
                                    )
                                  }
                                  style={{ lineHeight: 1 }}
                                >
                                  <i
                                    className={
                                      copiedKey === d.key_value
                                        ? "ti ti-check text-success"
                                        : "ti ti-copy text-muted"
                                    }
                                    style={{ fontSize: 16 }}
                                  ></i>
                                </button>
                              </div>
                            ) : (
                              <div className="text-muted">--</div>
                            )}

                            {d.detailsExtra && (
                              <small className="text-muted d-block">
                                {d.detailsExtra}
                              </small>
                            )}
                          </div>

                          {/* Timeline */}
                          <div className="col-lg-2 col-md-3 px-3 border-end">
                            <div className="cai-col-label">Timeline</div>
                            {d.status === "cancelled" ? (
                              <>
                                <div>
                                  <span className="fw-semibold">
                                    Cancelled By:
                                  </span>{" "}
                                  {d.last_updated_by}
                                </div>

                                <div>
                                  <span className="fw-semibold">Reason:</span>{" "}
                                  {d.reason}
                                </div>
                              </>
                            ) : (
                              <>
                                {d.start_date && (
                                  <div>
                                    <span className="fw-semibold">Start:</span>{" "}
                                    {dayjs(d.start_date).format("DD-MM-YYYY")}
                                  </div>
                                )}

                                {d.completed_date && (
                                  <div>
                                    <span className="fw-semibold">
                                      Completed:
                                    </span>{" "}
                                    {dayjs(d.completed_date).format(
                                      "DD-MM-YYYY",
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Status */}
                          <div className="col-lg-3 px-3 border-end text-center">
                            <Button
                              className="cai-status-btn"
                              style={{
                                background: d.color_value || "#6c757d",
                                color: "#fff",
                                border: "none",
                                borderRadius: "25px",
                                minWidth: "150px",
                                fontWeight: 600,
                              }}
                              onClick={() => {
                                const isOpen =
                                  activeCheck === d.assignment_template_id;

                                if (isOpen) {
                                  setActiveCheck(null);
                                } else {
                                  setActiveCheck(d.assignment_template_id);
                                  handleViewLog(d.assignment_template_id);
                                }
                              }}
                            >
                              {STATUS_CONFIG[d.status as CheckStatus]?.label ||
                                formatStatus(d.status)}

                              {activeCheck === d.assignment_template_id ? (
                                <CaretUpOutlined className="ms-2" />
                              ) : (
                                <CaretDownOutlined className="ms-2" />
                              )}
                            </Button>
                          </div>
                          <div className="col-lg-2 col-md-12 px-3 mt-3 mt-lg-0">
                            <div className="d-grid gap-2"> 
                              {(() => {
                                const hideActionButtons = [
                                  // "CANDIDATE_PENDING",
                                  // "CANDIDATE_IN_PROGRESS",
                                  "STOPPED",
                                ].includes(d.status);

                                const isWithoutValidation =
                                  /\(without validation\)/i.test(
                                    d.check_name ?? "",
                                  );

                                return !hideActionButtons ? (
                                  <>
                                    {/* Artifacts Button */}
                                    {(d.artifact === true ||
                                      d.artifact === "true") && (
                                      <button
                                        className="btn btn-outline-primary btn-sm fw-semibold"
                                        onClick={() => {
                                          navigate(
                                            all_routes.verifier_addArtifacts,
                                            {
                                              state: {
                                                case_id: d.case_id ?? "",
                                                assignment_id:
                                                  d.assignment_id ?? "",
                                                assignment_template_id:
                                                  d.assignment_template_id ??
                                                  "",
                                                check_id: d.check_id ?? "",
                                              },
                                            },
                                          );
                                        }}
                                      >
                                        <i className="ti ti-paperclip me-1"></i>
                                        Attach Artifact
                                      </button>
                                    )}

                                    {/* Insufficiency Button */}
                                    {(d.insuff === true ||
                                      d.insuff === "true") &&
                                      !isWithoutValidation && (
                                        <button
                                          className="btn btn-outline-warning btn-sm fw-semibold"
                                          onClick={() =>
                                            navigate(
                                              `${all_routes.verifier_insufficiencyDetails}`,
                                              {
                                                state: {
                                                  assignment_template_id:
                                                    d.assignment_template_id,
                                                  assignment_id:
                                                    d.assignment_id,
                                                  case_id: d.case_id,
                                                  candidate_id: d.candidate_id,
                                                },
                                              },
                                            )
                                          }
                                        >
                                          <i className="ti ti-alert-triangle me-1"></i>
                                          Raise Insuff
                                        </button>
                                      )}
                                  </>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {activeCheck === d.assignment_template_id && (
                        <div className="card cai-expand-card m-0 mt-0 w-100 p-2">
                          {/* <div className="cai-expand-header p-4 border-bottom mb-1">
                          <div className="d-flex align-items-center gap-3 pb-2">
                            <div className="fw-semibold">Check Status</div>
                             <span
                              className="badge cai-status-chip"
                              style={{
                                background: d.color_value,
                                color: "#fff",
                              }}
                            >
                              {formatStatus(d.status)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                              <div className="fw-semibold">Check Remarks</div>
                              <div className="text-muted">
                                {d.reason || "Not Available"}
                              </div>
                            </div>

                            <div>
                              <button
                                className="btn btn-sm btn-primary d-flex gap-2"
                                onClick={() =>
                                  handleDownloadReport(d.assignment_template_id)
                                }
                              >
                                <i className="bi bi-download"></i>
                                <span className="wip-btn-label">
                                  Download Artifacts
                                </span>
                              </button>
                            </div>
                          </div>
                        </div> */}
                          {getCaseLog.loading ? (
                            <Loading />
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-bordered mb-0 cai-log-table">
                                <thead className="table-light">
                                  <tr>
                                    <th>Title</th>
                                    <th>Description</th>
                                    <th>Actor Name</th>
                                    <th>Actor Type</th>
                                    <th style={{ width: "20%" }}>Event Time</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(caseLogs[d.assignment_template_id] || [])
                                    .length > 0 ? (
                                    caseLogs[d.assignment_template_id].map(
                                      (item: any) => (
                                        <tr key={item.event_id}>
                                          <td>{item.title}</td>

                                          <td>{item.description}</td>

                                          <td>{item.actor_name}</td>

                                          <td>{item.actor_type}</td>

                                          <td>
                                            {dayjs(item.event_time).format(
                                              "DD-MM-YYYY hh:mm A",
                                            )}
                                          </td>
                                        </tr>
                                      ),
                                    )
                                  ) : (
                                    <tr>
                                      <td colSpan={5} className="text-center">
                                        <span className="cai-no-timeline">
                                          No Timeline Found
                                        </span>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="card">
                  <div className="caf-empty-state">No Data Available</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CaseActionItems;
